import { TypeOfRequestLabel } from '@base/enums/request-label.enum';

export const BUSINESS_MATCHING_VALIDATION_ERROR_CODE = {
  INVALID_MISMATCH_MEETING_OPTION: 'INVALID_MISMATCH_MEETING_OPTION',
  INVALID_PREVIEW_TARGET_REAL_ACCOUNT: 'INVALID_PREVIEW_TARGET_REAL_ACCOUNT',
  INVALID: 'INVALID',
  PLATFORM_CLOSED: 'PLATFORM_CLOSED',
  TABLE_NOT_SET: 'TABLE_NOT_SET',
  SN_TABLE_FULL: 'SN_TABLE_FULL',
  ATTENDEE_LOGIN_REACH_MEETING_LIMIT: 'ATTENDEE_LOGIN_REACH_MEETING_LIMIT',
  ATTENDEE_LOGIN_REACH_COMPANY_MEETING_LIMIT: 'ATTENDEE_LOGIN_REACH_COMPANY_MEETING_LIMIT',
  MAX_HANDSHAKE_REACHED: 'MAX_HANDSHAKE_REACHED',
  MAX_HANDSHAKE_COMPANY_REACHED: 'MAX_HANDSHAKE_COMPANY_REACHED',
  NO_REMAINING_TIMESLOT: 'NO_REMAINING_TIMESLOT',
  ATTENDEE_LOGIN_TIMESLOT_FULL: 'ATTENDEE_LOGIN_TIMESLOT_FULL',
  OTHER_TIMESLOT_FULL: 'OTHER_TIMESLOT_FULL',
  OTHER_REACH_MEETING_LIMIT: 'OTHER_REACH_MEETING_LIMIT',
  OTHER_NOT_CONFIRM_TIMESLOT: 'OTHER_NOT_CONFIRM_TIMESLOT',
  SHOW_TIMESLOT_RECOMENDATION: 'SHOW_TIMESLOT_RECOMENDATION',
} as const;

export type BusinessMatchingValidationErrorCode =
  (typeof BUSINESS_MATCHING_VALIDATION_ERROR_CODE)[keyof typeof BUSINESS_MATCHING_VALIDATION_ERROR_CODE];

export const BUSINESS_MATCHING_VALIDATION_ERROR_DESCRIPTION_MAP: Record<BusinessMatchingValidationErrorCode, string> = {
  [BUSINESS_MATCHING_VALIDATION_ERROR_CODE.INVALID_MISMATCH_MEETING_OPTION]:
    'THE_REQUEST_CANNOT_COMPLETED_CAUSE_NO_SHARED_PREFERENCES',
  [BUSINESS_MATCHING_VALIDATION_ERROR_CODE.INVALID_PREVIEW_TARGET_REAL_ACCOUNT]: 'PREVIEW_ACCOUNT_LIMITED_INTERACTIONS',
  [BUSINESS_MATCHING_VALIDATION_ERROR_CODE.INVALID]: 'FAIL_LOAD_MEETINGS',
  [BUSINESS_MATCHING_VALIDATION_ERROR_CODE.PLATFORM_CLOSED]: 'THE_PLATFORM_IS_CLOSED_FOR_THE_EVENT',
  [BUSINESS_MATCHING_VALIDATION_ERROR_CODE.TABLE_NOT_SET]: 'THE_AVAILABLE_MEETING_AREA_FULLY_BOOKED',
  [BUSINESS_MATCHING_VALIDATION_ERROR_CODE.SN_TABLE_FULL]: 'THE_AVAILABLE_MEETING_AREA_FULLY_BOOKED',
  [BUSINESS_MATCHING_VALIDATION_ERROR_CODE.ATTENDEE_LOGIN_REACH_MEETING_LIMIT]:
    'YOU_HAVE_REACHED_THE_MAXIMUM_NUMBER_OF_MEETINGS_THAT_YOU_CAN_ARRANGE_MAX',
  [BUSINESS_MATCHING_VALIDATION_ERROR_CODE.ATTENDEE_LOGIN_REACH_COMPANY_MEETING_LIMIT]:
    'YOU_HAVE_REACHED_THE_MAXIMUM_NUMBER_OF_MEETINGS_THAT_YOUR_ORGANISATION_IS_ABLE_TO_ARRANGE',
  [BUSINESS_MATCHING_VALIDATION_ERROR_CODE.MAX_HANDSHAKE_REACHED]:
    'YOU_HAVE_REACHED_THE_MAXIMUM_NUMBER_OF_REQUESTS_THAT_YOU_CAN_SEND',
  [BUSINESS_MATCHING_VALIDATION_ERROR_CODE.MAX_HANDSHAKE_COMPANY_REACHED]:
    'YOU_HAVE_REACHED_THE_MAXIMUM_NUMBER_OF_REQUESTS_THAT_YOUR_ORGANISATION_IS_ABLE_TO_SEND',
  [BUSINESS_MATCHING_VALIDATION_ERROR_CODE.NO_REMAINING_TIMESLOT]:
    'NO_POSSIBLE_MEETING_TIMESLOTS_AS_THEY_ARE_FULLY_BOOKED',
  [BUSINESS_MATCHING_VALIDATION_ERROR_CODE.ATTENDEE_LOGIN_TIMESLOT_FULL]:
    'YOU_ARE_FULLY_BOOKED_AND_THERE_ARE_NO_OTHER_POSSIBLE_MEETING_TIMESLOTS',
  [BUSINESS_MATCHING_VALIDATION_ERROR_CODE.OTHER_TIMESLOT_FULL]: 'USER_IS_FULLY_BOOKED',
  [BUSINESS_MATCHING_VALIDATION_ERROR_CODE.OTHER_REACH_MEETING_LIMIT]:
    'USER_HAS_REACHED_THE_MAXIMUM_NUMBER_OF_MEETINGS_THAT_CAN_BE_MADE',
  [BUSINESS_MATCHING_VALIDATION_ERROR_CODE.OTHER_NOT_CONFIRM_TIMESLOT]:
    'THEY_HAVE_NOT_INDICATED_AVAILABILITY_AT_OTHER_TIMINGS',
  [BUSINESS_MATCHING_VALIDATION_ERROR_CODE.SHOW_TIMESLOT_RECOMENDATION]:
    'NO_POSSIBLE_MEETING_TIMESLOTS_AS_THEY_ARE_FULLY_BOOKED',
};

export const BUSINESS_MATCHING_VALIDATION_ERROR_TITLE_MAP: Record<
  BusinessMatchingValidationErrorCode,
  'HOLD_ON' | 'NO_TIME_AVAILABILITY'
> = {
  [BUSINESS_MATCHING_VALIDATION_ERROR_CODE.INVALID_MISMATCH_MEETING_OPTION]: 'HOLD_ON',
  [BUSINESS_MATCHING_VALIDATION_ERROR_CODE.INVALID_PREVIEW_TARGET_REAL_ACCOUNT]: 'HOLD_ON',
  [BUSINESS_MATCHING_VALIDATION_ERROR_CODE.INVALID]: 'HOLD_ON',
  [BUSINESS_MATCHING_VALIDATION_ERROR_CODE.PLATFORM_CLOSED]: 'HOLD_ON',
  [BUSINESS_MATCHING_VALIDATION_ERROR_CODE.TABLE_NOT_SET]: 'HOLD_ON',
  [BUSINESS_MATCHING_VALIDATION_ERROR_CODE.SN_TABLE_FULL]: 'NO_TIME_AVAILABILITY',
  [BUSINESS_MATCHING_VALIDATION_ERROR_CODE.ATTENDEE_LOGIN_REACH_MEETING_LIMIT]: 'HOLD_ON',
  [BUSINESS_MATCHING_VALIDATION_ERROR_CODE.ATTENDEE_LOGIN_REACH_COMPANY_MEETING_LIMIT]: 'HOLD_ON',
  [BUSINESS_MATCHING_VALIDATION_ERROR_CODE.MAX_HANDSHAKE_REACHED]: 'HOLD_ON',
  [BUSINESS_MATCHING_VALIDATION_ERROR_CODE.MAX_HANDSHAKE_COMPANY_REACHED]: 'HOLD_ON',
  [BUSINESS_MATCHING_VALIDATION_ERROR_CODE.NO_REMAINING_TIMESLOT]: 'NO_TIME_AVAILABILITY',
  [BUSINESS_MATCHING_VALIDATION_ERROR_CODE.ATTENDEE_LOGIN_TIMESLOT_FULL]: 'NO_TIME_AVAILABILITY',
  [BUSINESS_MATCHING_VALIDATION_ERROR_CODE.OTHER_TIMESLOT_FULL]: 'NO_TIME_AVAILABILITY',
  [BUSINESS_MATCHING_VALIDATION_ERROR_CODE.OTHER_REACH_MEETING_LIMIT]: 'HOLD_ON',
  [BUSINESS_MATCHING_VALIDATION_ERROR_CODE.OTHER_NOT_CONFIRM_TIMESLOT]: 'NO_TIME_AVAILABILITY',
  [BUSINESS_MATCHING_VALIDATION_ERROR_CODE.SHOW_TIMESLOT_RECOMENDATION]: 'NO_TIME_AVAILABILITY',
};

/** Values the host may pass to the MFE `action` attribute. */
export type BusinessMatchingMfeAction = 'REQUEST' | 'ACCEPT' | 'DECLINE' | 'UNDECLINE' | 'RESCHEDULE' | 'CANCEL';

export const BUSINESS_MATCHING_ACTION_MAP: Partial<Record<TypeOfRequestLabel, BusinessMatchingMfeAction>> = {
  [TypeOfRequestLabel.REQUEST_MEETING]: 'REQUEST',
  [TypeOfRequestLabel.ACCEPT_MEETING]: 'ACCEPT',
  [TypeOfRequestLabel.DECLINE_HANDSHAKE]: 'DECLINE',
  [TypeOfRequestLabel.UNDECLINE_HANDSHAKE]: 'UNDECLINE',
  [TypeOfRequestLabel.RESCHEDULE_MEETING]: 'RESCHEDULE',
  [TypeOfRequestLabel.CANCEL_MEETING]: 'CANCEL',
  // EDIT_REQUEST and REMOVE_REQUEST both show the REQUEST flow in the BM MFE.
  // The MFE internally detects the existing request state and renders cancel/edit options.
  [TypeOfRequestLabel.EDIT_REQUEST]: 'REQUEST',
  [TypeOfRequestLabel.REMOVE_REQUEST]: 'REQUEST',
};
